import React, { useState, useEffect } from 'react';
import { CustomizableSvgBackground } from './CustomizableSvgBackground';

/**
 * Theme-aware background component with overlay for dark/light mode
 * Uses customizable SVG with color scheme that can be changed
 * @param {Object} props
 * @param {string} props.colorScheme - Color scheme: 'gray', 'blue', 'purple', 'green', 'orange', 'red', 'teal'
 */
export const ThemeAwareSvgBackground = ({ colorScheme = 'gray' }) => {
  // Read initial dark mode from localStorage and DOM
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });

  // Listen for changes in localStorage and DOM class
  useEffect(() => {
    // Function to check and update dark mode
    const updateDarkMode = () => {
      const saved = localStorage.getItem('darkMode');
      const isDark = saved ? JSON.parse(saved) : false;
      const hasDarkClass = document.documentElement.classList.contains('dark');

      // Use both sources to be sure
      const shouldBeDark = isDark || hasDarkClass;

      setDarkMode(shouldBeDark);
    };

    // Initial check
    updateDarkMode();

    // Listen for storage events (when changed from other tabs/windows)
    window.addEventListener('storage', updateDarkMode);

    // Listen for DOM mutations to detect class changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          updateDarkMode();
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    // Cleanup
    return () => {
      window.removeEventListener('storage', updateDarkMode);
      observer.disconnect();
    };
  }, []);

  // Overlay configuration based on theme
  const overlayStyle = darkMode
    ? {
        // Dark mode: Strong dark overlay with subtle gradient
        background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.75) 0%, rgba(0, 0, 0, 0.65) 100%)',
      }
    : {
        // Light mode: Very subtle white overlay to brighten
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.05) 100%)',
      };



  return (
    <>
      {/* Base SVG Background - Fixed */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100vw',
          height: '100vh',
          zIndex: -2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          willChange: 'transform',
        }}
      >
        <CustomizableSvgBackground baseColor={colorScheme} opacity={1} />
      </div>

      {/* Theme Overlay with Gradient - Fixed */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100vw',
          height: '100vh',
          zIndex: -1,
          ...overlayStyle,
          transition: 'background 0.5s ease-in-out',
          pointerEvents: 'none',
          willChange: 'background',
        }}
      />
    </>
  );
};
